import React, { useEffect, useState } from "react";
import {
  UncontrolledDropdown,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
} from "reactstrap";
import { Modal } from "antd";
import { ThunderboltFilled, ArrowRightOutlined } from "@ant-design/icons";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Icon } from "../../../../components/Component";
import FormInput from "../../../form-components/InputComponent";
import { nanoid } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import miscService from "../../../../api/misc/misc";
import tenderApis from "../../../../api/tender/tenderApis";
import workCategoryApi from "../../../../api/master/workCategory";
import exportFromJSON from "export-from-json";
import sitesApis from "../../../../api/master/sites";
import SmartMapperFullscreen from "./SmartMapper/SmartMapperFullscreen";

const BLOB_BASE =
  "https://procurexuatstg.blob.core.windows.net/procurexuatblob";

const TEMPLATE_URLS: Record<string, string> = {
  Material: `${BLOB_BASE}/tender-materials-upload-template.xlsx`,
  "Service / Works": `${BLOB_BASE}/tender-tasks-upload-template.xlsx`,
};

const MATERIAL_TEMPLATE_BY_SEGMENT: Record<string, string> = {
  Villa: `${BLOB_BASE}/tender-materials-template-for-Villa-level-segment.xlsx`,
  default: `${BLOB_BASE}/tender-materials-template-for-flat-level-segment.xlsx`,
};

// Smart-mapper template selection — driven by the same site segment that
// drives the manual-template download.
//   Villa segment        → template 002 (villa-level intake)
//   Flat / Tower segment → template 003 (flat-level intake)
//   else (block / unknown / no site) → template 001 (generic materials)
const resolveSmartMapperTemplateId = (segment?: string | null): string => {
  const s = (segment || "").trim().toLowerCase();
  if (s === "villa") return "002";
  if (s === "flat" || s === "tower") return "003";
  return "001";
};

const SEGMENT_LABEL: Record<string, string> = {
  villa: "Villa-level",
  flat: "Flat-level",
  tower: "Tower-level",
  block: "Block-level",
};

const BulkMaterialUpload = ({
  append,
  tenderFor,
  siteId,
}: {
  append: (data: any) => void;
  tenderFor?: string;
  siteId?: string | number;
}) => {
  const [materials, setMaterials] = useState<any>([]);
  const [workCategories, setWorkCategories] = useState<any>([]);
  const [segmentLevel, setSegmentLevel] = useState<string | null>(null);
  const [siteName, setSiteName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [blockEdit, setBlockEdit] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [response, setResponse] = useState({
    status: "",
    message: "",
  });
  // Smart AI Mapper is gated by /smart-mapper/parse, which is fired once
  // on login and stored in Redux. The promo banner + fullscreen overlay
  // only appear for material uploads (not Service/Works) when the
  // session is authenticated for the intake_template agent.
  const smartMapperAuthenticated = useSelector(
    (state: any) => state.smartMapperAccess?.authenticated
  );
  const smartMapperAvailable =
    !!smartMapperAuthenticated && tenderFor !== "Service / Works";
  const [smartMapperFullscreen, setSmartMapperFullscreen] = useState(false);

  const methods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    watch,
    setValue,
  } = methods;
  const files = watch("attachment");

  useEffect(() => {
    setResponse({ status: "", message: "" });
  }, [files]);

  const fetchMaterialList = async () => {
    try {
      const { data, status } = await miscService.fetchProductFiltered();
      if (status) {
        const materialOption = data.map((item: any) => ({
          "Category Name": item?.categoryName,
          materialName: item?.productName,
          materialId: item?.materialID?.toString(),
        }));
        setMaterials(materialOption);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchWorkCategories = async () => {
    try {
      const res = await workCategoryApi.fetchWorkCategories();
      if (res?.status && res?.data) {
        const options = res.data.map((item: any) => ({
          categoryName: item.categoryName || item.category || item.name,
          categoryId: item.categoryId?.toString() || item.id?.toString(),
        }));
        setWorkCategories(options);
      }
    } catch (error) {
      console.log("Error fetching work categories:", error);
    }
  };

  const fetchSegmentLevel = async () => {
    if (!siteId) {
      setSegmentLevel(null);
      return;
    }
    try {
      const { data, status } = await sitesApis.fetchSegmentLevelBySiteId(siteId);
      if (status && data) {
        setSegmentLevel(typeof data === "string" ? data : data.segmentLevel || data.segment || null);
      } else {
        setSegmentLevel(null);
      }
    } catch (error) {
      console.log("Error fetching segment level:", error);
      setSegmentLevel(null);
    }
  };

  // Site name — surfaced in the Smart Mapper context banner so the user
  // can see which site this upload will be scoped to.
  const fetchSiteName = async () => {
    if (!siteId) {
      setSiteName(null);
      return;
    }
    try {
      const { data, status } = await sitesApis.fetchSiteWithBlockById(siteId);
      if (status && data) {
        setSiteName(data?.name || data?.siteName || null);
      } else {
        setSiteName(null);
      }
    } catch (error) {
      console.log("Error fetching site name:", error);
      setSiteName(null);
    }
  };

  useEffect(() => {
    fetchMaterialList();
    fetchWorkCategories();
  }, []);

  useEffect(() => {
    fetchSegmentLevel();
    fetchSiteName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  // Resolve the smart-mapper template ID + a friendly segment label for
  // the context banner. Done at render time so the banner reflects the
  // current segmentLevel even before it finishes loading.
  const smartMapperTemplateId = resolveSmartMapperTemplateId(segmentLevel);
  const smartMapperSegmentLabel =
    SEGMENT_LABEL[(segmentLevel || "").toLowerCase()] ||
    segmentLevel ||
    "Unknown";

  const handleUpload = async () => {
    try {
      const attachment = files;
      if (!attachment || !attachment[0]) {
        setResponse({
          status: "error",
          message: "Please select a file",
        });
        setBlockEdit(false);
        setLoading(false);
        return;
      }

      const _file = attachment[0];
      const formData = new FormData();
      formData.append("files", _file);
      formData.append(
        "tenderFor",
        tenderFor === "Service / Works" ? "SERVICE" : "MATERIAL"
      );
      if (segmentLevel) {
        formData.append("segmentLevel", segmentLevel);
      }
      if (siteId) {
        formData.append("siteId", siteId.toString());
      }

      const res = await tenderApis.bulkUploadValidate(formData);
      const { status, message, data } = res || {};

      if (status) {
        setResponse({
          status: "success",
          message: "File Uploaded Successfully",
        });
        const rows = data?.rows || [];
        rows.forEach((row: any) => {
          try {
            const item = JSON.parse(row.jsonData);
            if (tenderFor === "Service / Works") {
              append({
                key: nanoid(),
                taskTitle: item.taskTitle || "",
                workCategoryId: item.workCategoryId,
                workCategoryName: item.workCategoryName || "",
                quantity: item.quantity,
                uomId: item.uomId,
                uomName: item.uomName || "",
                taskUsage: item.usage || "",
                installationRequired: item.isInstallationRequired || false,
                description: item.description || "",
                workType: "Open",
              });
            } else {
              append({
                key: nanoid(),
                materialCategory: item.categoryId?.toString(),
                materialCategoryName: item.categoryName || "",
                materialName: item.materialId?.toString(),
                materialLabel: item.materialName || "",
                quantity: item.quantity,
                uomId: item.uomId,
                uomLabel: item.uomName || "",
                materialUsage: item.usage || "",
                installationRequired: item.isInstallationRequired || false,
                needByDate: item.needByDate ? dayjs(item.needByDate) : null,
                description: item.description || "",
                // Site hierarchy fields
                towerBlockVillaId: item.siteStructureUnitId ?? null,
                towerBlockVillaLabel: item.unitName || item.villaNo || "",
                floorNo: item.floorNo ?? null,
                floorLabel: item.floorNo ?? "",
                flatId: item.siteStructureSubUnitId ?? null,
                flatLabel: item.subUnitName || item.flatNo || "",
                locationId: item.locationId ?? null,
                locationLabel: item.locationName || "",
              });
            }
          } catch (e) {
            console.log("Error parsing row:", e);
          }
        });
        return;
      }
      setResponse({
        status: "error",
        message: message || "Failed to upload file",
      });
      setBlockEdit(false);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setResponse({
        status: "error",
        message: "Failed to upload file",
      });
      setBlockEdit(false);
      setLoading(false);
    }
  };

  return (
    <>
      <UncontrolledDropdown size={"md"}>
        <div
          onClick={() => {
            setShowModal(true);
          }}
          className="btn btn-white btn-outline-light"
        >
          <Icon name="upload-cloud"></Icon>
          <span>Import</span>
        </div>
      </UncontrolledDropdown>

      <Modal
        title="Bulk Material Upload"
        open={showModal}
        footer={null}
        onCancel={() => {
          setShowModal(false);
          setBlockEdit(false);
          methods.reset();
          setLoading(false);
          setResponse({ status: "", message: "" });
        }}
        width={"50%"}
        destroyOnClose
        maskClosable={false}
        keyboard={false}
      >
        {smartMapperAvailable && (
          <div
            onClick={() => setSmartMapperFullscreen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setSmartMapperFullscreen(true);
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 14px",
              marginBottom: 14,
              background:
                "linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)",
              border: "1px solid #dbeafe",
              borderRadius: 12,
              cursor: "pointer",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.boxShadow =
                "0 4px 12px -4px rgba(30, 58, 138, 0.18)";
              (e.currentTarget as HTMLDivElement).style.transform =
                "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
              (e.currentTarget as HTMLDivElement).style.transform = "none";
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background:
                  "linear-gradient(135deg, #1e3a8a 0%, #4338ca 100%)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 4px 12px -4px rgba(30, 58, 138, 0.4)",
              }}
            >
              <ThunderboltFilled style={{ fontSize: 18 }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: "#0f172a",
                  letterSpacing: -0.1,
                }}
              >
                Smart AI Mapper
                <span
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: "#4338ca",
                    background: "#ede9fe",
                    border: "1px solid #ddd6fe",
                    padding: "1px 6px",
                    borderRadius: 999,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                  }}
                >
                  New
                </span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#475569",
                  lineHeight: 1.5,
                  marginTop: 1,
                }}
              >
                Upload any Excel — AI maps your columns to the standard
                template automatically.
              </div>
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                background: "#1e3a8a",
                color: "#fff",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              Open <ArrowRightOutlined style={{ fontSize: 11 }} />
            </div>
          </div>
        )}
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(async () => {
              setResponse({ status: "", message: "" });
              setBlockEdit(true);
              setLoading(true);
              await handleUpload();
              setLoading(false);
            })}
          >
            <div style={{ marginBottom: "12px" }}>
              <p>
                You're about to create multiple Lines of Materials at once. To
                ensure a smooth import, please{" "}
                <a
                  href={
                    tenderFor === "Service / Works"
                      ? TEMPLATE_URLS["Service / Works"]
                      : siteId
                      ? MATERIAL_TEMPLATE_BY_SEGMENT[segmentLevel || ""] ||
                        MATERIAL_TEMPLATE_BY_SEGMENT["default"]
                      : TEMPLATE_URLS["Material"]
                  }
                  target="_self"
                  style={{
                    textDecoration: "underline",
                  }}
                >
                  Download Template
                </a>{" "}
                and fill in the required material data accurately and
                completely, then upload the file below.
              </p>
            </div>

            <div
              style={{
                backgroundColor: "#f8f9fa",
                padding: "10px",
                borderRadius: "6px",
                marginBottom: "12px",
                border: "1px solid #e9ecef",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#495057",
                  marginBottom: "6px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon
                  name="info"
                  style={{
                    marginRight: "6px",
                    fontSize: "14px",
                    display: "inline-flex",
                    alignItems: "center",
                    lineHeight: "1",
                  }}
                />
                <span
                  style={{
                    lineHeight: "1",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  Reference Data
                </span>
              </div>
              <p
                style={{
                  fontSize: "12px",
                  color: "#6c757d",
                  marginBottom: "0",
                }}
              >
                Download reference data for accurate data entry:{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (materials.length === 0) return;
                    const exportType = exportFromJSON.types.csv;
                    exportFromJSON({
                      data: materials,
                      fileName: "Materials",
                      exportType,
                    });
                  }}
                  style={{
                    textDecoration: "none",
                    color: "#2263b3",
                    fontWeight: "600",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <Icon name="file-text" style={{ marginRight: "4px" }} />
                  Materials
                </a>
                {" "}and{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (workCategories.length === 0) return;
                    const exportType = exportFromJSON.types.csv;
                    exportFromJSON({
                      data: workCategories,
                      fileName: "Work_Categories",
                      exportType,
                    });
                  }}
                  style={{
                    textDecoration: "none",
                    color: "#2263b3",
                    fontWeight: "600",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <Icon name="file-text" style={{ marginRight: "4px" }} />
                  Work Categories
                </a>
              </p>
            </div>

            <div className="text-right mt-3"></div>
            <p>
              <strong>Note : </strong> Do not modify column names or any
              existing data. All fields in the template are mandatory.
            </p>
            <Col md={4}>
              {files && files[0]?.name && (
                <div className="form-group m-0">
                  <div className="form-label-wrapper">
                    <label className="form-label" htmlFor={"attachment"}>
                      File Upload Area
                    </label>
                  </div>
                  <div
                    className="form-control-wrap d-flex align-items-center justify-content-between border p-1"
                    style={{ borderRadius: "3px" }}
                  >
                    <span>{files && files[0]?.name}</span>
                    {!blockEdit && (
                      <span
                        onClick={() => {
                          setValue("attachment", null);
                        }}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          display: "block",
                          fontSize: 14,
                        }}
                      >
                        <Icon name="cross"></Icon> &nbsp;
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div
                style={{
                  display: `${files && files[0]?.name ? "none" : "block"}`,
                }}
              >
                <FormInput
                  type="file"
                  name="attachment"
                  label="File Upload Area"
                  placeholder="File Upload Area"
                  accept=".xlsx"
                />
              </div>
            </Col>

            <Alert
              isOpen={response?.status === "error"}
              color="danger"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              {response?.message}
            </Alert>

            <Alert
              isOpen={response?.status === "success"}
              color="success"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <p>{response?.message}</p>
            </Alert>

            <Row className="mt-4">
              <Col>
                {response?.status === "success" ? (
                  <></>
                ) : (
                  <ul className="actions clearfix d-flex">
                    <li>
                      {loading ? (
                        <Button color="primary" type="button" size="md">
                          <Spinner size="sm" color="light" />
                        </Button>
                      ) : (
                        <Button color="primary" type="submit">
                          Upload
                        </Button>
                      )}
                    </li>
                    {!loading && (
                      <li className="m-3 mt-0">
                        <Button
                          color="primary"
                          onClick={() => {
                            setShowModal(false);
                            methods.reset();
                          }}
                          className="btn-dim btn btn-secondary"
                        >
                          Cancel
                        </Button>
                      </li>
                    )}
                  </ul>
                )}
              </Col>
            </Row>
          </form>
        </FormProvider>
      </Modal>

      <SmartMapperFullscreen
        open={smartMapperFullscreen}
        onClose={() => setSmartMapperFullscreen(false)}
        siteId={siteId}
        siteName={siteName}
        segmentLevel={segmentLevel}
        segmentLabel={smartMapperSegmentLabel}
        templateId={smartMapperTemplateId}
      />
    </>
  );
};

export default BulkMaterialUpload;
